import { ScenarioGraphNodeType } from 'types/api/scenarios/scenario'
import { type EvaluationConfiguration } from 'types/api/status-checks/evaluation'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'

/**
 * This is also used on the team integrations page, so the naming convention will be left as is,
 * since its usage there is not Health Check specific.
 */
export interface StatusCheckHeader {
  key: string
  value: string
  label?: string
}

export interface EndpointConfiguration {
  url?: string
  headers?: {
    [key: string]: string
  }
}

export interface EndpointResponse {
  httpStatusCode?: number
  requestLatencyMs?: number
  contentType?: string
  responseBody?: string
}

// EN-8466 to confirm: these are the strings the api expects
export enum StatusCheckCategory {
  ERROR_RATES = 'ERROR_RATES',
  LATENCY = 'LATENCY',
  THROUGHPUT = 'THROUGHPUT',
  UNKNOWN = 'UNKNOWN'
}

export interface StatusCheck {
  category?: StatusCheckCategory
  createdAt: string
  createdBy: string
  description?: string
  endpointConfiguration: EndpointConfiguration
  evaluationConfiguration: EvaluationConfiguration
  identifier: string
  isContinuous: boolean
  isPrivateNetwork: boolean
  name: string
  pollingIntervalSeconds?: number
  teamExternalIntegration: StatusCheckTeamExternalIntegration
  teamId: string
  thirdPartyPresets: string
  type?: ScenarioGraphNodeType
}

export interface StatusCheckTeamExternalIntegration {
  name: string
  type?: IntegrationType
  domain?: string
  observabilityToolType?: ObservabilityToolType
  integrationSpecificValues?: IntegrationSpecificValues
}

export interface StatusCheckCreateRequest {
  isContinuous?: boolean
  isPrivateNetwork?: boolean
  name: string
  description?: string
  thirdPartyPresets?: string
  endpointConfiguration: EndpointConfiguration
  evaluationConfiguration: EvaluationConfiguration
  teamExternalIntegration?: StatusCheckTeamExternalIntegration
  webhookId?: string
  rawEndpointConfiguration: EndpointConfiguration
}

export enum ObservabilityToolType {
  DATADOG = 'DATADOG',
  PAGERDUTY = 'PAGERDUTY',
  NEWRELIC = 'NEWRELIC',
  GRAFANA = 'GRAFANA',
  DYNATRACE = 'DYNATRACE',
  APPDYNAMICS = 'APPDYNAMICS',
  AWS = 'AWS',
  CUSTOM = 'CUSTOM'
}

export interface StatusCheckEvaluationPredicate {
  type?: PredicateType
  jqQuery?: string
  jpQuery?: string
  comparator?: string
  rValue?: any
}

export enum PredicateType {
  String = 'String',
  Number = 'Number',
  Boolean = 'Boolean'
}

export interface ResponseBodyEvaluation {
  op?: 'AND' | 'OR'
  predicates?: StatusCheckEvaluationPredicate[]
}

export interface StatusCheckEvaluationConfiguration {
  healthyStatusCode: string
  requestTimeout: number
  responseBodyEvaluation?: ResponseBodyEvaluation
}

export interface ServiceStatusCheck {
  category?: StatusCheckCategory
  description?: string
  evaluationConfiguration: StatusCheckEvaluationConfiguration
  id?: string
  integration: TeamExternalIntegration
  isAutogenerated?: boolean
  isContinuous: boolean
  name: string
  pollingIntervalSeconds?: number
}

export enum TeamExternalIntegrationAuthenticationStatus {
  Authenticated = 'AUTHENTICATED',
  UnAuthenticated = 'UNAUTHENTICATED',
  Untested = 'UNTESTED'
}

enum IntegrationSpecificKeys {
  AWS_ACCESS_KEY_ID = 'AWS_ACCESS_KEY_ID',
  AWS_SECRET_ACCESS_KEY = 'AWS_SECRET_ACCESS_KEY',
  AWS_IAM_ROLE_ARN = 'AWS_IAM_ROLE_ARN'
}

export type IntegrationSpecificValues = {
  [key in IntegrationSpecificKeys]?: string
}

export interface TeamExternalIntegration {
  type: IntegrationType
  name: string
  id?: string
  url?: string
  headers?: StatusCheckHeader[]
  isPrivateNetworkIntegration?: boolean
  isConfigured?: boolean
  authUrl?: string
  lastAuthenticationStatus?: TeamExternalIntegrationAuthenticationStatus
  canBeUsedAsStatusCheck?: boolean
  createdAt: Date | string
  sortIndex?: number
  isOther?: boolean
  observabilityToolType?: ObservabilityToolType
  domain?: string
  integrationSpecificValues?: IntegrationSpecificValues
}

export interface TeamExternalIntegrationResponse {
  userId: string
  type: IntegrationType
  observabilityToolType: ObservabilityToolType
  createdAt: Date | string
  domain: string
  url: string
  headers: StatusCheckHeader[]
  name: string
  teamId: string
  privateNetwork: boolean
  canBeUsedAsStatusCheck: boolean
  lastAuthenticationStatus: TeamExternalIntegrationAuthenticationStatus
  integrationSpecificValues: IntegrationSpecificValues
}
